import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "auth";
import { useSelector } from "react-redux";
import redeem_services from "services/redeem_services";
import {
  networkTypeSelector,
  treasuryIdSelector,
} from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import type {
  RedeemRequest,
  ApproveRedeemPayload,
  RejectRedeemPayload,
} from "types/redeem";

export const REJECT_REASON = "admin rejected the request";

export function useRedeemData() {
  const queryClient = useQueryClient();
  const treasuryId = useSelector(treasuryIdSelector);
  const networkType = useSelector(networkTypeSelector);
  const { user } = useAuth();
  const { param: token } = useSettlementToken();

  // Filtered to the selected token server-side, and the backend applies it DURING the tail walk,
  // so `limit` still yields a full page of that token rather than a handful. Token in the key
  // because the response genuinely differs per token.
  const requestsQuery = useQuery<RedeemRequest[]>({
    queryKey: ["redeemRequests", treasuryId, token],
    queryFn: () =>
      redeem_services.getRedeemRequests(treasuryId!, networkType, token),
    enabled: !!user && !!treasuryId,
  });

  // The searched-for redeem may be a looked-up one that isn't in the recent list, so its
  // cached lookup has to be refreshed too or the row keeps its pre-action status.
  const invalidateRedeems = () => {
    queryClient.invalidateQueries({ queryKey: ["redeemRequests"] });
    queryClient.invalidateQueries({ queryKey: ["redeem-lookup"] });
  };

  const approveMutation = useMutation({
    mutationFn: (payload: ApproveRedeemPayload) =>
      redeem_services.approveRedeem(payload, networkType),
    onSuccess: invalidateRedeems,
  });

  const rejectMutation = useMutation({
    mutationFn: (payload: RejectRedeemPayload) =>
      redeem_services.rejectRedeem(payload, networkType),
    onSuccess: invalidateRedeems,
  });

  return {
    requests: requestsQuery.data ?? [],
    requestsLoading: requestsQuery.isLoading,
    refreshRequests: () =>
      queryClient.invalidateQueries({ queryKey: ["redeemRequests"] }),
    pendingCount: (requestsQuery.data ?? []).filter(
      (r: RedeemRequest) => r.status === 0,
    ).length,
    approveRedeem: approveMutation.mutate,
    rejectRedeem: rejectMutation.mutate,
  };
}
